import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import JSZip from 'jszip';

import { saveBlob } from 'helper/downloadFile';
import { handleApiError } from 'helper/error';
import { sendInterfaceErrorToSentry } from 'helper/sendErrorToSentry';
import { downloadFileViewerFile } from 'services/fileDownloadApi';
import { ResponseError } from 'services/api/base';
import { FileList } from 'helper/products/fileViewer';
import {
  ExplorerFile,
  buildExplorerTree,
  flattenTree,
  isExpiredTokenError,
  pluralize,
  zipEntryNames,
  zipFileName,
} from 'helper/products/fileExplorer';

export const DOWNLOAD_ERROR_MESSAGE = 'Não foi possível baixar o arquivo. Verifique sua conexão e tente novamente.';
/** Downloads simultâneos ao montar o .zip: rápido sem enfileirar o servidor. */
export const ZIP_CONCURRENCY = 3;

export interface ZipProgress {
  done: number;
  total: number;
}

interface Params {
  /** Pede a lista de novo (tokens novos) e devolve a árvore crua. */
  refresh: () => Promise<FileList | undefined>;
  notify: (message: string) => void;
}

type DownloadResult = Blob | ResponseError;

const goneMessage = (name: string) => `“${name}” não está mais disponível. A lista foi atualizada.`;

const isSessionError = (error: ResponseError) => error.data === 'JWT Inválido';

/** Tokens de download atuais, por chave estável (`pasta/arquivo`). */
const fileIdsByKey = (list: FileList | undefined) => new Map(
  flattenTree(buildExplorerTree(list ?? []))
    .filter((node): node is ExplorerFile => node.kind === 'file')
    .map((file) => [file.key, file.id]),
);

/**
 * Download de arquivos do "Meus Arquivos", sem tirar o usuário da página por causa de um arquivo.
 *
 * O `file-id` é um token com TTL (4 h no backend). Vencido, a API responde 403 "link expirou" — que
 * o fluxo genérico (`handleApiError`) levava a `/forbidden`. Aqui o hook renova a lista uma vez,
 * acha o mesmo arquivo pela chave estável e repete com o token novo. O 404 (arquivo apagado depois
 * que a lista foi carregada) também fica na página: avisa e atualiza a lista.
 */
export default function useFileViewerDownload({ refresh, notify }: Params) {
  const navigate = useNavigate();
  const [downloadingKeys, setDownloadingKeys] = useState<Set<string>>(() => new Set());
  const [zipProgress, setZipProgress] = useState<ZipProgress | null>(null);
  const zipCancelled = useRef(false);

  const setDownloading = useCallback((key: string, active: boolean) => {
    setDownloadingKeys((current) => {
      const next = new Set(current);
      if (active) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const reportNetworkFailure = useCallback((file: ExplorerFile, error: unknown) => {
    notify(DOWNLOAD_ERROR_MESSAGE);
    sendInterfaceErrorToSentry('Falha no download de arquivo', file.name, error, {
      fingerprint: ['file-download-failed'],
      tags: { produto: 'file-viewer' },
    });
  }, [notify]);

  const downloadFile = useCallback(async (file: ExplorerFile) => {
    setDownloading(file.key, true);
    try {
      let result: DownloadResult = await downloadFileViewerFile(file.id);

      if (!(result instanceof Blob) && isExpiredTokenError(result)) {
        const freshId = fileIdsByKey(await refresh()).get(file.key);
        if (!freshId) {
          notify(goneMessage(file.name));
          return;
        }
        result = await downloadFileViewerFile(freshId);
      }

      if (result instanceof Blob) {
        saveBlob(result, file.name);
        return;
      }

      if (Number(result.status) === 404 || isExpiredTokenError(result)) {
        notify(goneMessage(file.name));
        refresh();
        return;
      }

      handleApiError(navigate, result);
    } catch (error) {
      reportNetworkFailure(file, error);
    } finally {
      setDownloading(file.key, false);
    }
  }, [navigate, notify, refresh, reportNetworkFailure, setDownloading]);

  const cancelZip = useCallback(() => {
    zipCancelled.current = true;
  }, []);

  /**
   * Seleção múltipla vira um .zip montado no navegador (o backend não tem rota de zip): cada
   * arquivo vem pelo `get-file`, `ZIP_CONCURRENCY` por vez. Token vencido renova a lista UMA vez
   * para o lote inteiro. Falha isolada não derruba o lote: o .zip sai com o que deu certo.
   */
  const downloadZip = useCallback(async (files: ExplorerFile[], folderPath: string[]) => {
    if (files.length === 0) return;
    if (files.length === 1) {
      await downloadFile(files[0]);
      return;
    }

    zipCancelled.current = false;
    setZipProgress({ done: 0, total: files.length });

    const zip = new JSZip();
    const entryNames = zipEntryNames(files);
    const failed: string[] = [];
    let added = 0;
    let done = 0;
    let freshIds: Promise<Map<string, string>> | null = null;
    let sessionError: ResponseError | null = null;

    const fetchOne = async (file: ExplorerFile): Promise<DownloadResult> => {
      const result = await downloadFileViewerFile(file.id);
      if (result instanceof Blob || !isExpiredTokenError(result)) return result;
      freshIds = freshIds ?? refresh().then(fileIdsByKey);
      const freshId = (await freshIds).get(file.key);
      return freshId ? downloadFileViewerFile(freshId) : result;
    };

    const queue = [...files];
    const worker = async () => {
      while (queue.length && !zipCancelled.current && !sessionError) {
        const file = queue.shift() as ExplorerFile;
        try {
          // eslint-disable-next-line no-await-in-loop
          const result = await fetchOne(file);
          if (result instanceof Blob) {
            zip.file(entryNames.get(file.key) as string, result);
            added += 1;
          } else if (isSessionError(result)) {
            sessionError = result;
          } else {
            failed.push(file.name);
          }
        } catch (error) {
          failed.push(file.name);
        }
        done += 1;
        setZipProgress({ done, total: files.length });
      }
    };

    try {
      await Promise.all(Array.from({ length: Math.min(ZIP_CONCURRENCY, files.length) }, worker));

      if (sessionError) {
        handleApiError(navigate, sessionError);
        return;
      }
      if (zipCancelled.current) {
        notify('Download cancelado.');
        return;
      }
      if (added === 0) {
        notify('Nenhum arquivo pôde ser baixado. Tente novamente.');
        return;
      }

      const blob = await zip.generateAsync({ type: 'blob' });
      saveBlob(blob, zipFileName(folderPath, new Date()));

      if (failed.length) {
        notify(`.zip pronto com ${pluralize(added, 'arquivo', 'arquivos')}. `
          + `${pluralize(failed.length, 'arquivo não pôde ser incluído', 'arquivos não puderam ser incluídos')}.`);
        sendInterfaceErrorToSentry('Arquivos fora do .zip do Meus Arquivos', failed.join(', '), null, {
          fingerprint: ['file-viewer-zip-partial'],
          level: 'warning',
        });
      } else {
        notify(`.zip com ${pluralize(added, 'arquivo', 'arquivos')} pronto.`);
      }
    } catch (error) {
      notify('Não foi possível montar o .zip. Tente novamente.');
      sendInterfaceErrorToSentry('Falha ao montar .zip do Meus Arquivos', `${files.length} arquivos`, error, {
        fingerprint: ['file-viewer-zip-failed'],
      });
    } finally {
      setZipProgress(null);
    }
  }, [downloadFile, navigate, notify, refresh]);

  return {
    downloadingKeys,
    downloadFile,
    zipProgress,
    downloadZip,
    cancelZip,
  };
}
